
import React, { useState, useMemo } from 'react';
import { NomineeDiscoveryItem, UserProfile, AssetCategory, Asset } from '../types';
import { 
  ShieldCheck, 
  Building2, 
  FileText, 
  ArrowRight, 
  CheckCircle2, 
  AlertTriangle,
  MapPin,
  Clock,
  ChevronRight,
  X,
  Plus,
  TrendingUp,
  Shield,
  Briefcase,
  Home,
  PiggyBank,
  Layers,
  History,
  Lock,
  UserCheck,
  Activity,
  HeartPulse,
  Smartphone,
  Stamp,
  // Add missing icons
  Coins,
  Cpu
} from 'lucide-react';

interface NomineePortalProps {
  user: UserProfile;
  inheritances: NomineeDiscoveryItem[];
}

const CATEGORY_MAP: Record<string, { icon: any, color: string }> = {
  [AssetCategory.SAVINGS]: { icon: PiggyBank, color: 'bg-blue-50 text-blue-600' },
  [AssetCategory.CURRENT]: { icon: Building2, color: 'bg-slate-50 text-slate-600' },
  [AssetCategory.FD]: { icon: Layers, color: 'bg-indigo-50 text-indigo-600' },
  [AssetCategory.RD]: { icon: Layers, color: 'bg-teal-50 text-teal-600' },
  [AssetCategory.LOCKER]: { icon: Lock, color: 'bg-amber-50 text-amber-600' },
  [AssetCategory.INVESTMENT]: { icon: TrendingUp, color: 'bg-purple-50 text-purple-600' },
  [AssetCategory.MUTUAL_FUND]: { icon: TrendingUp, color: 'bg-orange-50 text-orange-600' },
  [AssetCategory.INSURANCE]: { icon: Shield, color: 'bg-red-50 text-red-600' },
  [AssetCategory.LIFE_INSURANCE]: { icon: Shield, color: 'bg-red-50 text-red-600' },
  [AssetCategory.RETIREMENT]: { icon: History, color: 'bg-amber-50 text-amber-600' },
  [AssetCategory.EPF]: { icon: History, color: 'bg-indigo-50 text-indigo-600' },
  [AssetCategory.PROPERTY]: { icon: Home, color: 'bg-emerald-50 text-emerald-600' },
  [AssetCategory.EMPLOYER]: { icon: Briefcase, color: 'bg-indigo-50 text-indigo-600' },
  [AssetCategory.NSC]: { icon: Stamp, color: 'bg-rose-50 text-rose-600' },
  [AssetCategory.DIGITAL_GOLD]: { icon: Coins, color: 'bg-amber-50 text-amber-600' },
  [AssetCategory.CRYPTO]: { icon: Cpu, color: 'bg-orange-50 text-orange-600' },
};

const maskIdentifier = (id: string | undefined) => {
  if (!id) return 'Not Provided';
  if (id.length <= 4) return '****' + id;
  return 'XXXX' + id.slice(-4);
};

export const NomineePortal: React.FC<NomineePortalProps> = ({ user, inheritances }) => {
  const [selectedEstateIndex, setSelectedEstateIndex] = useState<number | null>(inheritances.length > 0 ? 0 : null);
  const [selectedCategory, setSelectedCategory] = useState<AssetCategory | 'All' | null>(null);

  const activeEstate = selectedEstateIndex !== null ? inheritances[selectedEstateIndex] : null;

  const categories = useMemo(() => {
    if (!activeEstate) return [];
    const catMap = new Map<AssetCategory | string, any>();
    activeEstate.assets.forEach(asset => {
      const existing = catMap.get(asset.category) || { count: 0, share: 0, status: asset.status || 'Verified' };
      catMap.set(asset.category, {
        count: existing.count + 1,
        share: Math.max(existing.share, asset.nomineePercentage),
        status: existing.status === 'Verified' ? asset.status || 'Verified' : existing.status
      });
    });
    return Array.from(catMap.entries()).map(([name, data]) => ({ name, ...data }));
  }, [activeEstate]);

  const filteredAssets = useMemo(() => {
    if (!activeEstate || !selectedCategory) return [];
    if (selectedCategory === 'All') return activeEstate.assets;
    return activeEstate.assets.filter(a => a.category === selectedCategory);
  }, [activeEstate, selectedCategory]);

  if (inheritances.length === 0) {
    return (
      <div className="max-w-4xl mx-auto py-20 text-center animate-fade-in px-4">
        <div className="w-24 h-24 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-6"><FileText size={32} className="text-slate-300" /></div>
        <h2 className="text-2xl font-bold text-slate-800">No Heritage Records Found</h2>
        <p className="text-slate-500 mt-4 max-w-md mx-auto">We couldn't find any estates securely linked to <b>{user.email}</b>.</p>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 pb-20 animate-fade-in">
      {/* Heritage Selector */}
      {inheritances.length > 1 && (
        <div className="flex gap-3 mb-8 overflow-x-auto pb-4 no-scrollbar">
          {inheritances.map((estate, idx) => (
            <button key={idx} onClick={() => { setSelectedEstateIndex(idx); setSelectedCategory(null); }} className={`flex-shrink-0 px-5 py-2.5 rounded-2xl font-bold text-sm transition-all border whitespace-nowrap ${selectedEstateIndex === idx ? 'bg-teal-600 text-white border-teal-600 shadow-lg' : 'bg-white text-slate-600 border-slate-200 hover:border-teal-300'}`}>Estate of {estate.ownerName}</button>
          ))}
        </div>
      )}

      {activeEstate && (
        <div className="space-y-10">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
            <div>
              <div className="flex items-center gap-2 text-teal-600 font-bold text-[10px] uppercase tracking-[0.2em] mb-1"><ShieldCheck size={14} /> Secure Heritage Portal</div>
              <h1 className="text-3xl font-bold text-slate-900 leading-tight">Heritage from {activeEstate.ownerName}</h1>
              <p className="text-slate-500 mt-2">Your legal nominations and succession roadmaps.</p>
            </div>
            <div className="bg-white border border-slate-200 p-5 rounded-[32px] flex items-center gap-4 shadow-sm">
               <div className="w-12 h-12 rounded-full bg-emerald-500 flex items-center justify-center text-white"><CheckCircle2 size={24} /></div>
               <div><div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Master Status</div><div className="text-lg font-bold text-slate-800">Ready to Claim</div></div>
            </div>
          </div>

          {/* 1. Category Tiles */}
          <section>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xs font-bold text-slate-400 uppercase tracking-[0.2em] flex items-center gap-2"><Plus size={12} className="text-teal-500" /> Assets in Registry</h2>
              <button onClick={() => setSelectedCategory('All')} className="text-xs font-bold text-teal-600 hover:text-teal-700 flex items-center gap-1">View All <ArrowRight size={12} /></button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {categories.map((cat, i) => {
                const config = CATEGORY_MAP[cat.name] || { icon: Building2, color: 'bg-slate-50 text-slate-600' };
                const Icon = config.icon;
                return (
                  <button key={i} onClick={() => setSelectedCategory(cat.name as AssetCategory)} className={`p-8 rounded-[40px] text-left transition-all group border-2 ${selectedCategory === cat.name ? 'border-teal-500 bg-teal-50 shadow-xl' : 'border-white bg-white shadow-sm hover:border-slate-100'}`}>
                    <div className="flex justify-between items-start mb-8">
                      <div className={`p-4 rounded-3xl transition-colors ${selectedCategory === cat.name ? 'bg-teal-600 text-white shadow-lg' : config.color}`}><Icon size={28} /></div>
                      <span className={`text-[10px] px-3 py-1 rounded-full font-bold ${cat.status === 'Verified' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>{cat.status.toUpperCase()}</span>
                    </div>
                    <h3 className="text-xl font-bold text-slate-900 mb-1">{cat.name}</h3>
                    <div className="flex items-center justify-between mt-3 text-slate-500"><div className="text-sm font-medium">{cat.count} Records</div><div className="text-teal-600 font-bold text-sm">{cat.share}% Share</div></div>
                  </button>
                );
              })}
            </div>
          </section>

          {/* ASSET DETAIL VIEW */}
          {selectedCategory && (
            <div className="animate-fade-in space-y-8 bg-slate-50/50 p-8 rounded-[48px] border border-slate-100">
               <div className="flex items-center justify-between">
                  <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-3">
                    <span className={`p-2 rounded-xl shrink-0 ${selectedCategory === 'All' ? 'bg-slate-900 text-white' : (CATEGORY_MAP[selectedCategory]?.color || 'bg-slate-200 text-slate-600')}`}>
                      {selectedCategory === 'All' ? <Layers size={24} /> : React.createElement(CATEGORY_MAP[selectedCategory]?.icon || Building2, { size: 24 })}
                    </span>
                    {selectedCategory} Records
                  </h2>
                  <button onClick={() => setSelectedCategory(null)} className="p-2 bg-white rounded-full text-slate-400 hover:text-slate-900 shadow-sm border border-slate-100"><X size={20} /></button>
               </div>

               <div className="space-y-6">
                  {filteredAssets.map((asset, i) => {
                    const sector = asset.category.toLowerCase();
                    const isInsurance = sector.includes('insurance');
                    const isRetirement = sector.includes('retirement') || sector.includes('pension');
                    const isFintech = sector.includes('fintech');
                    const isProperty = sector.includes('property');

                    return (
                    <div key={i} className="bg-white rounded-[40px] border border-slate-200 overflow-hidden shadow-sm">
                        <div className="p-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
                           <div className="lg:col-span-4 space-y-6">
                              <div><h4 className="text-2xl font-bold text-slate-800">{asset.name}</h4><p className="text-slate-500 mt-1 font-medium">{asset.institution || 'Institutional Provider'}</p></div>
                              <div className="space-y-3">
                                 <div className="p-5 bg-teal-50 rounded-[32px] border border-teal-100">
                                    <div className="text-[10px] font-bold text-teal-600 uppercase tracking-widest mb-2">Heritage Metadata</div>
                                    <div className="grid grid-cols-2 gap-y-4">
                                       <div><div className="text-[8px] font-bold text-slate-400 uppercase">{isInsurance ? 'Policy Type' : isProperty ? 'Property Type' : 'Account Type'}</div><div className="text-sm font-bold text-slate-800">{asset.accountType || 'Generic'}</div></div>
                                       <div><div className="text-[8px] font-bold text-slate-400 uppercase">Your Allocation</div><div className="text-sm font-bold text-slate-800">{asset.nomineePercentage}%</div></div>
                                       {isInsurance && asset.policyStatus && (<div><div className="text-[8px] font-bold text-slate-400 uppercase">Policy Status</div><div className={`text-sm font-bold ${asset.policyStatus === 'Active' ? 'text-emerald-600' : 'text-rose-600'}`}>{asset.policyStatus}</div></div>)}
                                       {isInsurance && asset.sumAssuredRange && (<div><div className="text-[8px] font-bold text-slate-400 uppercase">Sum Assured</div><div className="text-sm font-bold text-slate-800">{asset.sumAssuredRange}</div></div>)}
                                       <div><div className="text-[8px] font-bold text-slate-400 uppercase">Holding Mode</div><div className="text-sm font-bold text-slate-800">{asset.modeOfOperation || 'Single'}</div></div>
                                       <div><div className="text-[8px] font-bold text-slate-400 uppercase">Verified Linked?</div><div className={`text-sm font-bold ${asset.nominationStatus ? 'text-emerald-600' : 'text-amber-600'}`}>{asset.nominationStatus ? 'Yes' : 'Review Required'}</div></div>
                                    </div>
                                 </div>
                                 {asset.customerId && (
                                   <div className="p-4 bg-slate-50 rounded-xl border border-slate-100 flex items-center justify-between">
                                      <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{isInsurance ? 'Policy No' : isRetirement ? 'UAN / PRAN' : 'Reference ID'}</div>
                                      <div className="text-sm font-black text-slate-700">{maskIdentifier(asset.customerId)}</div>
                                   </div>
                                 )}
                              </div>
                           </div>

                           <div className="lg:col-span-4 bg-slate-50 p-8 rounded-[40px] space-y-4">
                              <h5 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest flex items-center gap-2"><MapPin size={12} className="text-teal-500" /> Claim Roadmap</h5>
                              <div className="space-y-4">
                                 {[ 
                                   { i: Building2, l: 'Institution', v: asset.institution || 'Registered Provider' },
                                   { i: MapPin, l: isProperty ? 'City Location' : 'Contact Unit', v: asset.branchDetails || 'Main Office' },
                                   { i: FileText, l: 'Process Mode', v: asset.claimPath || 'Hybrid' }, 
                                   { i: Clock, l: 'Est. Timeline', v: asset.estimatedTimeline || '10–20 Business Days' } 
                                 ].map((item, idx) => (
                                    <div key={idx} className="flex gap-3">
                                      <div className="w-8 h-8 rounded-lg bg-white flex items-center justify-center text-teal-600 shadow-sm border border-slate-100 shrink-0"><item.i size={16} /></div>
                                      <div className="min-w-0 flex-1"><div className="text-[8px] text-slate-400 font-bold uppercase">{item.l}</div><div className="text-sm font-bold text-slate-800 truncate">{item.v}</div></div>
                                    </div>
                                 ))}
                              </div>
                           </div>

                           <div className="lg:col-span-4 space-y-6">
                              <h5 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest flex items-center gap-2"><ShieldCheck size={12} className="text-teal-500" /> Verification Protocol</h5>
                              <div className="space-y-4">
                                 <div className="space-y-2">
                                    <div className="text-[8px] font-bold text-slate-400 uppercase">Documents Needed</div>
                                    {[isProperty ? 'Ownership Deed' : 'Death Certificate', 'Nominee PAN / Aadhaar', isInsurance ? 'Original Policy' : isRetirement ? 'PF/Pension Claim Form' : 'Verification Form'].map((doc, idx) => (
                                      <div key={idx} className="flex items-start gap-2 text-[11px] font-semibold text-slate-600"><CheckCircle2 size={12} className="text-teal-500 mt-0.5" /> <span>{doc}</span></div>
                                    ))}
                                 </div>
                                 <div className="p-5 bg-white border border-slate-100 rounded-2xl shadow-inner">
                                    <div className="flex items-center gap-2 mb-2"><History size={14} className="text-teal-500" /><span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Ownership Sync</span></div>
                                    <div className="text-xs font-bold text-slate-700">{asset.lastUpdated ? new Date(asset.lastUpdated).toLocaleDateString() : 'Sync Required'}</div>
                                 </div>
                                 {isFintech && asset.userInstructions && (
                                   <div className="p-4 bg-teal-50 border border-teal-100 rounded-2xl"><div className="text-[8px] font-bold text-teal-600 uppercase mb-1">Access Instructions</div><p className="text-[10px] text-teal-800 leading-relaxed italic">{asset.userInstructions}</p></div>
                                 )}
                              </div>
                           </div>
                        </div>
                        <div className="px-10 py-4 bg-slate-900 flex flex-col sm:flex-row justify-between items-center text-white gap-3">
                           <div className="text-[10px] font-bold tracking-widest opacity-60">Fiducary ID: {asset.id.toUpperCase()}</div>
                           <button className="flex items-center gap-1 text-[10px] font-bold hover:text-teal-400 uppercase tracking-widest">Secure Claim Portal <ChevronRight size={14} /></button>
                        </div>
                    </div>
                  )})}
               </div>
            </div>
          )}

          {/* Footer */}
          {!selectedCategory && (
            <div className="bg-teal-900 rounded-[48px] p-16 text-white relative overflow-hidden">
               <div className="relative z-10">
                  <h3 className="text-4xl font-bold mb-6">Estate Readiness Confirmed.</h3>
                  <p className="text-teal-100 text-lg leading-relaxed mb-8 max-w-lg">Your legacy dashboard is active. In a transition event, institutions will require the specific claim roadmap provided for each asset.</p>
                  <div className="flex flex-col sm:flex-row gap-5">
                    <button className="bg-teal-500 hover:bg-teal-400 text-white font-bold px-8 py-4 rounded-2xl transition-all shadow-xl">Download Claim Guide</button>
                    <button className="bg-white/10 hover:bg-white/20 text-white font-bold px-8 py-4 rounded-2xl transition-all border border-white/20">Request Assistance</button>
                  </div>
               </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
